// xmlDecoder.ts

/**
 * Update <w:alias w:val="..."> for each content control (<w:sdt>)
 * to append "_&%<id>" where <id> is read from <w:id w:val="...">.
 *
 * This version avoids XPath and uses namespace-aware DOM methods,
 * which are reliable in browsers and many Node DOM polyfills.
 */

/* ------------ Public API ------------ */

/**
 * Main entry point: provide an HTML-escaped OOXML string,
 * get back the same format with aliases updated.
 */
export function updateContentControlAliases(ccsXmlString: string): string {
  // 2) Parse as XML
  const xml = parseXml(ccsXmlString);

  // 3) Update aliases using namespace-aware DOM traversal
  processContentControls(xml);

  // 4) Serialize and re-encode
  return new XMLSerializer().serializeToString(xml);
}

/* ------------ Internals ------------ */

// WordprocessingML "w:" namespace URI used throughout the document
const NS_W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

/**
 * Parse string into an XML Document. Throws on parse errors.
 */
function parseXml(xmlText: string): Document {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");

  // Basic parse error check (browser DOMParser drops a <parsererror> node into the doc)
  const parseError = doc.getElementsByTagName("parsererror")[0];
  if (parseError) {
    // Some engines put details in textContent
    throw new Error("XML parsing failed: " + (parseError.textContent || "Unknown error"));
  }
  return doc;
}

/**
 * Traverse <w:sdt> nodes and update/insert <w:alias w:val="..."> under <w:sdtPr>.
 */
function processContentControls(xml: Document): void {
  // Get all <w:sdt> elements (namespace-aware)
  const sdtNodes = xml.getElementsByTagNameNS(NS_W, "sdt");

  for (let i = 0; i < sdtNodes.length; i++) {
    const sdt = sdtNodes[i];

    // Find the first <w:sdtPr> inside this <w:sdt>
    const sdtPrList = sdt.getElementsByTagNameNS(NS_W, "sdtPr");
    if (sdtPrList.length === 0) continue;
    const sdtPr = sdtPrList[0];

    // Find <w:id w:val="...">
    const idList = sdtPr.getElementsByTagNameNS(NS_W, "id");
    if (idList.length === 0) continue;
    const idEl = idList[0];

    const idVal =
      idEl.getAttributeNS(NS_W, "val") || // proper namespaced attribute
      idEl.getAttribute("w:val") || // fallback if serializer kept prefix
      "";

    if (!idVal) continue;

    // Find or create <w:alias>
    let aliasEl = sdtPr.getElementsByTagNameNS(NS_W, "alias").item(0);
    if (!aliasEl) {
      aliasEl = xml.createElementNS(NS_W, "w:alias");
      // insert near the top of <w:sdtPr> (before first child)
      sdtPr.insertBefore(aliasEl, sdtPr.firstChild);
    }

    // Read existing alias value (if any)
    const currentAlias = aliasEl.getAttributeNS(NS_W, "val") || aliasEl.getAttribute("w:val") || "";

    // Append behavior: "existing_&%<id>" or if empty, start with "_&%<id>"
    const newAlias = `${currentAlias ? currentAlias + "_" : "_"}&%${idVal}`;

    // Set namespaced attribute using the "w:" qualified name
    aliasEl.setAttributeNS(NS_W, "w:val", newAlias);
  }
}
